import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {onValue, ref} from 'firebase/database';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faMagnifyingGlass, faXmark, faDoorOpen, faArrowRight, faUser} from '@fortawesome/free-solid-svg-icons';
import {db} from '../../utils/firebaseConfig';
import {navItemsFor} from './navItems';
import {useAuth} from '../../contexts/AuthContext';
import {useClickOutside} from '../../hooks/useClickOutside';
import {toTenantArray} from '../../utils/tenantList';

interface Hit {
    key: string;
    label: string;
    hint: string;
    to: string;
    icon: typeof faDoorOpen;
}

interface TenantEntry {
    key: string;
    roomId: string;
    name: string;
    phone: string;
}

/**
 * Ô tìm kiếm ở header: tìm phòng (theo số, tên, mô tả), tìm người thuê (theo tên
 * hoặc SĐT), và nhảy thẳng tới trang menu.
 *
 * Tên và mô tả phòng lấy từ `roomDirectory` (node nhẹ) chứ không phải `rooms` -
 * ô tìm kiếm luôn hiện trên mọi trang, nên nó không được phép kéo về cả hồ sơ
 * phòng chỉ để lọc theo tên.
 *
 * Tên người thuê thì BẮT BUỘC phải đọc `rooms/{id}/tenants`, và cố ý KHÔNG gộp
 * vào roomDirectory: node đó `.read: true` - đọc được không cần đăng nhập, vì
 * trang đăng nhập cần nó để đổ ô chọn phòng. Nhét tên với SĐT người thuê vào đó
 * là công khai cho cả internet.
 *
 * Đổi lại, `rooms` chỉ được đăng ký khi chủ nhà THẬT SỰ gõ vào ô tìm kiếm lần
 * đầu - mở app rồi không tìm gì thì không tốn gì cả.
 */
export const GlobalSearch: React.FC<{ className?: string }> = ({className = ''}) => {
    const navigate = useNavigate();
    const {role} = useAuth();
    const [rooms, setRooms] = useState<Record<string, { name: string; description?: string }>>({});
    const [tenants, setTenants] = useState<TenantEntry[] | null>(null);
    const [wantsTenants, setWantsTenants] = useState(false);
    const [term, setTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Chỉ nhân viên đọc được `rooms` (rules); người thuê không có ô này nhưng
    // cứ chặn ở đây cho chắc, tránh bắn một request chắc chắn bị từ chối.
    const isStaff = role === 'admin' || role === 'manager' || role === 'viewer';

    useEffect(() => onValue(ref(db, 'roomDirectory'), snapshot => {
        setRooms(snapshot.exists() ? snapshot.val() : {});
    }, () => setRooms({})), []);

    // Gõ phát đầu tiên là bật danh sách người thuê, và giữ luôn cho cả phiên -
    // lần tìm thứ hai không phải chờ lại.
    useEffect(() => {
        if (isStaff && term.trim()) setWantsTenants(true);
    }, [isStaff, term]);

    // Listener chứ không phải get(): chủ nhà vừa sửa hồ sơ phòng xong quay ra
    // tìm thì phải ra tên mới, không phải tên của lúc gõ lần đầu.
    useEffect(() => {
        if (!wantsTenants) return;
        return onValue(ref(db, 'rooms'), snapshot => {
            const value: Record<string, { tenants?: unknown }> = snapshot.val() ?? {};
            setTenants(Object.entries(value).flatMap(([roomId, room]) =>
                toTenantArray(room?.tenants)
                    .filter(tenant => !!tenant.name)
                    .map((tenant, index) => ({
                        key: `tenant-${roomId}-${tenant.id || index}`,
                        roomId,
                        name: tenant.name,
                        phone: tenant.phoneNumber ?? '',
                    }))));
        }, () => setTenants([]));
    }, [wantsTenants]);

    useClickOutside(containerRef, isOpen, () => setIsOpen(false));

    // Ctrl/⌘+K để nhảy vào ô tìm kiếm
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                inputRef.current?.focus();
                setIsOpen(true);
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, []);

    const hits = useMemo<Hit[]>(() => {
        const needle = term.trim().toLowerCase();
        if (!needle) return [];

        const roomHits: Hit[] = Object.entries(rooms)
            .filter(([roomId, room]) =>
                roomId.toLowerCase().includes(needle) ||
                (room?.name || '').toLowerCase().includes(needle) ||
                (room?.description || '').toLowerCase().includes(needle))
            .slice(0, 6)
            .map(([roomId, room]) => ({
                key: `room-${roomId}`,
                label: room?.name || `Phòng ${roomId}`,
                // Hiện mô tả chứ không phải câu "Mở hồ sơ phòng" vô nghĩa: phòng
                // lọt vào kết quả nhờ khớp mô tả thì phải thấy được chỗ khớp,
                // không thì chủ nhà nhìn một phòng lạ mà chẳng hiểu vì sao.
                hint: room?.description?.trim() || 'Mở hồ sơ phòng',
                to: `/phong/${roomId}`,
                icon: faDoorOpen,
            }));

        // SĐT tìm được luôn: chủ nhà có số trong máy nhưng hay quên tên/phòng.
        // Bỏ khoảng trắng hai bên chứ không bỏ hết - "09 12" gõ nhầm thì không
        // nên khớp bừa vào một số nào đó.
        const tenantHits: Hit[] = (tenants ?? [])
            .filter(tenant =>
                tenant.name.toLowerCase().includes(needle) ||
                (!!tenant.phone && tenant.phone.includes(needle)))
            .slice(0, 6)
            .map(tenant => ({
                key: tenant.key,
                label: tenant.name,
                hint: `Người thuê · ${rooms[tenant.roomId]?.name || `Phòng ${tenant.roomId}`}`,
                to: `/phong/${tenant.roomId}`,
                icon: faUser,
            }));

        const pageHits: Hit[] = navItemsFor(role)
            .filter(item => item.label.toLowerCase().includes(needle))
            .map(item => ({
                key: `nav-${item.path}`,
                label: item.label,
                hint: item.subtitle,
                to: item.path,
                icon: item.icon,
            }));

        return [...roomHits, ...tenantHits, ...pageHits];
    }, [term, rooms, tenants, role]);

    useEffect(() => setActiveIndex(0), [term]);

    const go = (hit?: Hit) => {
        const target = hit ?? hits[activeIndex];
        if (!target) return;
        navigate(target.to);
        setTerm('');
        setIsOpen(false);
        inputRef.current?.blur();
    };

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape') {
            setIsOpen(false);
            inputRef.current?.blur();
        } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex(index => Math.min(index + 1, hits.length - 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex(index => Math.max(index - 1, 0));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            go();
        }
    };

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none"
            />
            <input
                ref={inputRef}
                type="text"
                value={term}
                onChange={(e) => {
                    setTerm(e.target.value);
                    setIsOpen(true);
                }}
                onFocus={() => setIsOpen(true)}
                onKeyDown={onKeyDown}
                placeholder="Tìm phòng, người thuê..."
                aria-label="Tìm kiếm"
                className="glass-input w-full pl-10 pr-9 py-2 text-sm"
            />
            {term && (
                <button
                    onClick={() => {
                        setTerm('');
                        inputRef.current?.focus();
                    }}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full
                        flex items-center justify-center text-slate-500 hover:text-slate-600 dark:hover:text-slate-200"
                    aria-label="Xóa tìm kiếm"
                >
                    <FontAwesomeIcon icon={faXmark} className="text-xs"/>
                </button>
            )}

            {isOpen && term.trim() && (
                <div className="absolute left-0 right-0 top-full mt-2 z-50 glass-panel
                    overflow-hidden max-h-80 overflow-y-auto scrollbar-thin">
                    {hits.length === 0 ? (
                        /* Danh sách người thuê về sau ô tìm kiếm một nhịp. Báo
                           "Không tìm thấy" ngay lúc đó là nói sai - tên người ta
                           gõ có thể đang nằm trong mẻ dữ liệu chưa về. */
                        wantsTenants && tenants === null ? (
                            <p className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                                Đang tìm…
                            </p>
                        ) : (
                            <p className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                                Không tìm thấy "{term}"
                            </p>
                        )
                    ) : (
                        hits.map((hit, index) => (
                            <button
                                key={hit.key}
                                onMouseEnter={() => setActiveIndex(index)}
                                onClick={() => go(hit)}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors
                                    ${index === activeIndex ? 'bg-slate-500/15' : 'hover:bg-white/50 dark:hover:bg-white/5'}`}
                            >
                                <FontAwesomeIcon icon={hit.icon} className="w-4 text-slate-500 flex-shrink-0"/>
                                <span className="min-w-0 flex-1">
                                    <span className="block text-sm font-medium text-slate-800 dark:text-white truncate">
                                        {hit.label}
                                    </span>
                                    <span className="block text-xs text-slate-500 dark:text-slate-400 truncate">
                                        {hit.hint}
                                    </span>
                                </span>
                                <FontAwesomeIcon icon={faArrowRight} className="text-xs text-slate-500 flex-shrink-0"/>
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};
